import React, { useEffect, useState, useRef, ReactNode, useContext } from "react";
import { connect } from "react-redux";
import { Card } from "react-md";
import MSplitter from "components/Shared/MSplitter";
import MGrid, { ACTION_ADD } from "components/Shared/MGrid";
import { DATA_TYPE_DATE_TIME } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CONTENT_LIBRARY_EDIT } from "typeConstants/rights";
import { getMarkdownResources, getTopicsList } from "requests/cms";
import { InitialState } from "reducers/initialState";
import { MarkdownResourceResponse, TopicResponse } from "requests/cms/interfaces";
import ContentPane from "./ContentPane";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { useStateSorted } from "hooks/useStateSorted";
import { PhoenixFormListItem } from "interfaces/Mueller.Business.Models";
import { getDesignerFormsList } from "requests/phoenixForms";

export const phoenixProjectId = "1A5AA9F7-150E-4B6D-9809-4F7CBD683736";

interface Props {
  // Redux
  canEditContent: boolean;
}

const ContentManagementList = ({ canEditContent }: Props) => {
  const mounted = useRef(true);

  const [isCreating, setIsCreating] = useState(false);
  const [isEditingMarkdown, setIsEditingMarkdown] = useState(false);
  const isEditingMarkdownRef = useRef(isEditingMarkdown);
  isEditingMarkdownRef.current = isEditingMarkdown; // have to use a ref because of MGrid on row click

  const [contentList, setContentList] = useState<TopicResponse[]>();
  const [formsList, setFormsList] = useStateSorted<PhoenixFormListItem>(null, "name");
  const [listUpdatedAt, setListUpdatedAt] = useState<number>();
  const [selectedTopicId, setSelectedTopicId] = useState<string>();
  const selectedTopic = selectedTopicId && contentList.find(t => t.topicId === selectedTopicId);

  const [resources, setResources] = useState<MarkdownResourceResponse[]>();

  const dialogContext = useContext<DialogProviderValue>(DialogContext);

  const updateTopicList = () => getTopicsList(phoenixProjectId, setContentList, mounted);

  const refreshResourceList = () => {
    setResources(null);
    getMarkdownResources(setResources, mounted);
  };

  useEffect(() => {
    updateTopicList();
    refreshResourceList();
    getDesignerFormsList({ customerNumber: null, includeInactive: false }, setFormsList, mounted);

    return () => {
      mounted.current = false;
    };
  }, []);

  // grid wont update if its data has changed but the number of rows hasnt. this is a sure way to notice when the list changes, and then swap the key on the grid, forcing it to update
  useEffect(() => setListUpdatedAt(Date.now()), [contentList]);

  const isLoading = !contentList;
  
  const showAreYouSureIfCurrentlyEditing = (callback: () => void) => {
    if (isEditingMarkdown) {
      dialogContext.showYesNo("Editing Content", "You are currently editing content. Are you sure you want to switch topics? You will lose unsaved changes", callback);
    } else {
      callback();
    }
  };

  const onRowClick = (index: number, areYouSure = false) => {
    showAreYouSureIfCurrentlyEditing(() => setSelectedTopicId(contentList[index].topicId));
  };

  const clearSelectedContent = () => {
    showAreYouSureIfCurrentlyEditing(() => {
      setIsEditingMarkdown(false);
      if (isCreating) {
        setIsCreating(false);
      } else {
        setSelectedTopicId(null);
      }
    });
  };

  const addContent = () => {
    setIsCreating(true);
  };

  let secondTitle = "Content Information";
  let secondChild: ReactNode;

  if (selectedTopic) {
    secondChild = (
      <ContentPane
        key="viewing-topic"
        topic={selectedTopic}
        resources={resources}
        refreshList={updateTopicList}
        onMarkdownEditChanged={setIsEditingMarkdown}
        refreshResources={refreshResourceList}
        formsList={formsList}
      />
    );
  }

  if (isCreating) {
    secondTitle = "Creating Content";
    secondChild = (<ContentPane 
        key="creating-content" 
        isCreating 
        refreshList={updateTopicList} 
        onCreateFinished={(response: TopicResponse) => {
          setIsCreating(false);
          setSelectedTopicId(response.topicId);
        }}
      />);
  }

  return (
    <MSplitter
      firstChild={
        <div className="padding-page-content full-height">
          <Card className="full-height">
            <MGrid
              key={listUpdatedAt}
              headerText="Content Library"
              columnDefinitions={columns}
              data={contentList}
              isFetchingData={isLoading}
              onRowClick={i => onRowClick(i)} // purposely wrapped so the areYouSure doesnt get set to something from MGrid on click
              actions={canEditContent && [{ code: ACTION_ADD, helpText: "Add Content", onClick: addContent }]}
              displayNumOfItemsFound
            />
          </Card>
        </div>}
      secondChild={secondChild}
      secondChildTitle={{
        title: secondTitle,
        buttons: [
          { icon: "close", onClick: clearSelectedContent }
        ]
      }}
    />
  );

};

const columns = [
  { key: "code", name: "Code", dataSource: "uniqueKey" },
  { key: "name", name: "Name", dataSource: "name" },
  { key: "category", name: "Category", dataSource: "category" },
  { key: "description", name: "Description", dataSource: "description" },
  { key: "createDate", name: "Create Date", dataType: DATA_TYPE_DATE_TIME, dataSource: "createDate" },
  { key: "lastUpdate", name: "Last Update", dataType: DATA_TYPE_DATE_TIME, dataSource: "modifiedDate" },
  { key: "publishDate", name: "Published Date", dataType: DATA_TYPE_DATE_TIME, dataSource: "publishDate" },
];

const mapStateToProps = (state: InitialState) => ({
  canEditContent: userHasRight(CONTENT_LIBRARY_EDIT)(state),
});

export default connect(mapStateToProps)(ContentManagementList);
